import { ImageVariants } from '@apparel/ui/images/Image/ImageProps';
import { I18n } from '@components/Trans';
import { createIntersect } from '@lib/lazy';
import { createStyled } from '@lib/style/createStyled';
import { prop } from '@lib/style/theme';
import { unitStrict } from '@lib/style/unit';
import { classList } from '@lib/utils';
import ButtonBase from '@material-ui/core/ButtonBase';
import Icon from '@material-ui/core/Icon';
import Paper from '@material-ui/core/Paper';
import RootRef from '@material-ui/core/RootRef';
import { createEvent } from '@packages/events/lib/createEvent';
import React, { HTMLAttributes, useEffect, useRef } from 'react';
import { Lightbox } from '../../lightbox';
import { getImageSource } from './getImageSource';
import { ImageObject } from './types';

export interface ImageProps extends HTMLAttributes<HTMLElement> {
    source: string | File | ImageObject;
    imageSet?: string[] | File[] | ImageObject[];
    alt?: string;
    className?: string;
    childrenClassName?: string;
    fallback?: string;
    preview?: boolean;
    previewIndex?: number;
    previewButton?: boolean;
    placeholder?: string;
    rounded?: boolean | number | string;
    variant?: ImageVariants;
    elevation?: number;
    width?: any;
    height?: any;
    /**
     * Tell to the image to not destroy the object when image get unmount
     */
    noDestroyObject?: boolean;
    fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down' | 'unset' | 'inherit' | 'initial';
    rootProps?: any;
    passRef?: (event: any) => void;
}

/**
 * Create radius style helper
 * @type {*}
 */
const radius = prop('rounded', 0, (rounded) => {
    if (!rounded) {
        rounded = 0;
    }
    if (typeof rounded !== 'string' && typeof rounded !== 'number') {
        rounded = '50%';
    }

    return unitStrict(rounded);
});

/**
 * Detect object fit prop
 * @type {*}
 */
const fit = prop('fit', 'unset');

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: Image
 * @date:  29.08.2018
 * @time:  09:35
 * @refactor: 12.04.2019 (15:41) <Better image support>
 * @scope:
 * @deprecated
 */
export const Image = (props: ImageProps) => {
    const {
        placeholder,
        preview,
        previewButton = true,
        previewIndex,
        alt,
        imageSet,
        source,
        elevation,
        fallback,
        variant,
        passRef,
        className,
        childrenClassName,
        rounded,
        onErrorCapture,
        rootProps,
        noDestroyObject,
        ...rest
    } = props;
    // $FlowFixMe
    const [source$, isObjectCreated] = getImageSource(source as any, variant, placeholder as any);
    const ref = useRef<HTMLImageElement>(null);

    const onIntersect = ({ target }) => {
        if (source$) {
            target.src = source$;
            target.removeEventListener('intersect', onIntersect);
            window.dispatchEvent(createEvent('resize', { skipDrawer: true }));
        }
    };

    /**
     * Intersect event, add source to image when image intersect with window.
     */
    useEffect(() => {
        if (passRef) {
            passRef(ref);
        }

        const unListen = createIntersect(ref, onIntersect);

        return () => {
            if (ref.current) {
                unListen();

                if (isObjectCreated && source$ && !noDestroyObject) {
                    URL.revokeObjectURL(source$);
                }
            }
        };
    }, [ref.current, source$]);

    /**
     * Handle image error
     * @param e
     */
    const handleError = (e) => {
        let fallback$ = fallback;
        if (typeof onErrorCapture === 'function') {
            const res = onErrorCapture(e) as any;
            if (res) {
                fallback$ = res;
            }
        }

        if (typeof fallback$ === 'string') {
            e.target.src = fallback$;
        } else if (typeof fallback$ === 'object') {
            const [source$] = getImageSource(fallback$, variant);
            e.target.src = source$;
        }
    };

    const image$ = (
        <RootRef rootRef={ref}>
            <ImageStyle
                className={classList('ui__image', className, childrenClassName, { rounded })}
                component={'img'}
                rounded={rounded}
                alt={alt || ''}
                onErrorCapture={handleError}
                elevation={elevation || 0}
                {...rest}
            />
        </RootRef>
    );

    const definedSet = imageSet && imageSet.length > 0;

    return preview ? (
        <RootStyle {...rootProps} rounded={rounded}>
            <Lightbox images={imageSet ?? [source$]} showThumbnails={definedSet} showImageCount={definedSet}>
                <ButtonBase className={'preview__button'} {...{ index: previewIndex }}>
                    <div className="preview__image">{image$}</div>

                    {(ref.current ? ref.current.clientWidth : parseInt(rest.width as any) || 0) >= 140 &&
                    previewButton ? (
                        <div className="preview__inner">
                            <Icon className={'preview__icon'}>remove_red_eye</Icon>
                            <I18n variant={'button'} className={'preview__text'} uppercase>
                                Preview
                            </I18n>
                        </div>
                    ) : null}
                </ButtonBase>
            </Lightbox>
        </RootStyle>
    ) : (
        image$
    );
};

export const ImageStyle = createStyled(
    Paper,
    ['rounded', 'fit'],
    ({ css }) => css`
        && {
            user-select: none;
            border-radius: ${radius};
            object-fit: ${fit};
        }
    `,
);

// $FlowFixMe
const RootStyle = createStyled(
    'div',
    ['rounded'],
    ({ css }) => css`
        & {
            position: relative;
            overflow: hidden;
            border-radius: ${radius};

            .preview {
                &__button {
                    display: inline-block;
                    font-size: 0;
                    background-color: #ddd;
                }

                &__image {
                    position: relative;
                    z-index: 1;
                }

                &__inner {
                    position: relative;
                    z-index: 3;
                    padding: 10px 8px;
                    display: flex;
                    align-items: center;
                    align-content: center;
                    justify-content: center;
                }

                &__icon,
                &__text {
                    color: #555;
                }

                &__icon {
                    width: 20px;
                    height: 20px;
                    font-size: 20px;
                }

                &__text {
                    display: inline-block;
                    padding-left: 8px;
                    font-size: 14px;
                    line-height: 1;
                    padding-top: 1px;
                }
            }
        }
    `,
);

export { getImageSource };
